import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";

import { renderErrorOrLoading } from "../commons/renderErrorOrLoading";
import type { ResultValue } from "../data/types";
import { useRow } from "../data/useRow";
import { MatchupImage } from "./MatchupImage";
import { Odds } from "./Odds";

interface MatchupViewProps {
  date?: Date;
  stage?: string;
  title?: string;
  home?: string;
  away?: string;
  score?: string;
  description?: string;
  image?: string;
  oddsHome?: number;
  oddsDraw?: number;
  oddsAway?: number;
  result?: ResultValue;
  multiplier?: number;
  index?: number;
  onOpen: () => void;
}

type MatchupHeaderProps = Pick<MatchupViewProps, "date" | "stage">;
type MatchupBodyProps = Pick<
  MatchupViewProps,
  "away" | "description" | "home" | "image" | "score" | "title"
>;
type MatchupActionsProps = Pick<
  MatchupViewProps,
  "multiplier" | "oddsAway" | "oddsDraw" | "oddsHome" | "result"
> & {
  currentPick?: ResultValue;
};

export function MatchupView({
  title,
  description,
  home,
  away,
  stage,
  score,
  onOpen,
  image,
  multiplier,
  oddsHome,
  oddsAway,
  oddsDraw,
  result,
  date,
  index,
}: MatchupViewProps): JSX.Element {
  const { myRow, error, loading } = useRow();
  const errorOrLoading = renderErrorOrLoading({ loading, error });
  const currentPick = pickForMatchup(myRow, index);

  if (errorOrLoading) return errorOrLoading;

  return (
    <Box
      style={{
        width: "100%",
      }}
    >
      <MatchupHeader date={date} stage={stage} />

      <Card
        onClick={onOpen}
        sx={{
          maxWidth: 380,
          minWidth: 0,
          width: "100%",
        }}
      >
        <CardActionArea>
          <MatchupBody
            away={away}
            description={description}
            home={home}
            image={image}
            score={score}
            title={title}
          />
        </CardActionArea>
        <MatchupActions
          currentPick={currentPick}
          multiplier={multiplier}
          oddsAway={oddsAway}
          oddsDraw={oddsDraw}
          oddsHome={oddsHome}
          result={result}
        />
      </Card>
    </Box>
  );
}

function MatchupHeader({ date, stage }: MatchupHeaderProps): JSX.Element {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      gap={2}
      sx={{ mb: 0.5 }}
    >
      <OptionalHeaderText value={date?.toDateString()} />
      <OptionalHeaderText value={stage} />
    </Stack>
  );
}

function OptionalHeaderText({ value }: { value?: string }): JSX.Element {
  if (!value) return <span />;

  return (
    <Typography variant="body2" color="text.secondary">
      {value}
    </Typography>
  );
}

function MatchupBody({
  away,
  description,
  home,
  image,
  score,
  title,
}: MatchupBodyProps): JSX.Element {
  const primaryTitle = matchupTitle({ away, home, title });
  const secondaryTitle = title && title !== primaryTitle ? title : undefined;

  return (
    <>
      <MatchupImage home={home} away={away} title={primaryTitle} image={image} />
      <CardContent>
        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Typography gutterBottom variant="h5" component="h2">
            {primaryTitle}
          </Typography>
          {score && (
            <Typography
              gutterBottom
              variant="h5"
              component="p"
              sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
            >
              {score}
            </Typography>
          )}
        </Stack>
        {secondaryTitle && (
          <Typography variant="body2" color="text.secondary">
            {secondaryTitle}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </CardContent>
    </>
  );
}

function MatchupActions({
  currentPick,
  multiplier,
  oddsAway,
  oddsDraw,
  oddsHome,
  result,
}: MatchupActionsProps): JSX.Element {
  return (
    <CardActions disableSpacing>
      <Odds
        oddsHome={oddsHome}
        oddsAway={oddsAway}
        oddsDraw={oddsDraw}
        result={result}
        myRow={currentPick}
      />

      {Boolean(multiplier) && (
        <Box sx={{ ml: "auto", pr: 1 }} data-testid="matchup-multiplier">
          x {multiplier}
        </Box>
      )}
    </CardActions>
  );
}

function matchupTitle({
  away,
  home,
  title,
}: Pick<MatchupViewProps, "away" | "home" | "title">): string {
  const teams = [home, away].filter(Boolean).join(" - ");

  return teams || title || "";
}

function pickForMatchup(
  row: string | undefined,
  index: number | undefined,
): ResultValue | undefined {
  return row && index !== undefined ? (row[index] as ResultValue) : undefined;
}
